import { useRecoilValue } from "recoil";
import { CityType, CountryType, CustomerRequestType, CustomerType, PermissionsType, SelectedParametersType, StateType, UserType } from "../../../types/types";
import { AbcOutlined, AttachFileOutlined, BadgeOutlined, CheckCircleRounded, CloseOutlined, DeleteForeverOutlined, DoNotDisturbOnRounded, DriveFileRenameOutlineOutlined, EmailOutlined, HelpOutlineOutlined, HideImageOutlined, InfoOutlined, KeyOutlined, LocalAirportOutlined, LocationCityOutlined, ManageAccountsOutlined, ManOutlined, Person, PersonOutlineOutlined, PhoneOutlined, PinOutlined, PlaceOutlined, RemoveCircleOutlineOutlined, SaveOutlined, Settings } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip, FormControl, FormGroup, Grid, IconButton, InputLabel, Paper, Skeleton, Stack, styled, Switch, SwitchProps, TextField, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ContentProps, Toast } from "primereact/toast";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import { CNPJFormatter, cpfFormatter, formatterZipCode, getFilteredList, phoneNumberFormatter, setToastRefToOpen } from "../../../functions/functions";

import customerfindAllCrudResponse from "../../../resources/Api/customer/customerfindAllCrudResponse";
import findAllCitiesByStateIdListResponse from "../../../resources/Api/city/findAllCitiesByStateIdListResponse";
import findAllStatesByCountryIdListResponse from "../../../resources/Api/state/findAllStatesByCountryIdListResponse";
import findAllCountriesListResponse from "../../../resources/Api/country/findAllCountriesListResponse";
import GridTemplate from "../../../components/GridTemplate";
import customerFindAllExportResponse from "../../../resources/Api/customer/customerFindAllExportResponse";
import customerUpdateStatusById from "../../../resources/Api/customer/customerUpdateStatusById";
import Rmodal from "../../../components/Rmodal";
import dayjs from "dayjs";
import { ArrowDropDownIcon, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import customerOnSaveData from "../../../resources/Api/customer/customerOnSaveData";
import customerOnUpdateData from "../../../resources/Api/customer/customerOnUpdateData";
import { userAtom } from "../../../atoms/atoms";
import { MENUS_ITENS } from "../../../consts/consts";
import findAllPlacesWithIqLocation from "../../../resources/Api/location/findAllPlacesWithIqLocation";
import CustomerFeaturesModal from "./CustomerFeaturesModal";
import useCepAutofill from "../../../hooks/useCepAutofill";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Ativo"}
                icon={
                    <KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ color: "rgb(121,182,162)", background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded
                            className="activated"
                            style={{ height: 28, width: 28 }}
                        />
                    ) : (
                        <DoNotDisturbOnRounded
                            className="deactivated"
                            style={{ height: 28, width: 28 }}
                        />
                    )
                }
            />
        ),
    },
    {
        field: "reducedName",
        align: "left",
        width: 250,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Nome"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row?.reducedName?.trim() ? item.row.reducedName : item.row?.name ?? "-"}
            />
        ),
    },
    {
        field: "cpf",
        width: 200,
        align: "left",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"Documento"}
                icon={
                    <BadgeOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row?.cnpj?.trim() ? CNPJFormatter(item.row?.cnpj) : cpfFormatter(item.row?.cpf ?? "")}
            />
        ),
    },
    {
        field: "email",
        align: "left",
        width: 250,
        headerClassName: "last-column",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{
                    color: "#fff",
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={"E-mail"}
                icon={
                    <EmailOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                }
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{
                    background: "transparent",
                    fontFamily: "Prometo",
                }}
                label={item.row.email}
            />
        ),
    },
    {
        field: "accessIcon",
        sortable: false,
        headerName: "",
        width: 460,
        renderCell: (item) => {
            const getItemPermission = (id: number): PermissionsType => {
                return item.row.permissions.find((permission: PermissionsType) => {
                    if (permission.optionItem.id === id) {
                        return permission;
                    };
                });
            };

            // const secretaryMenuItemId = 7;
            const secretaryPermission = getItemPermission(Number(MENUS_ITENS.SECRETARIES));
            // const accessesMenuItemId = 13;
            const accessesPermission = getItemPermission(Number(MENUS_ITENS.ACCESSES));

            return (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 2 }}>
                    {secretaryPermission?.canView && !item.row.useCpf && <Chip
                        className="chip-access"
                        sx={{
                            fontFamily: "Prometo",
                            background: "#00B700",
                            color: "#ffff",
                            padding: 1,
                        }}
                        onClick={() => {
                            item.row.navigate?.("/customers/secretaries", item.id, secretaryPermission);
                        }}
                        label="Secretarias"
                        icon={
                            <LocationCityOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                        }
                    />}

                    {accessesPermission?.canView && <Chip
                        className="chip-access"
                        sx={{
                            fontFamily: "Prometo",
                            background: "#00B700",
                            color: "#ffff",
                            padding: 1,
                        }}
                        onClick={() => {
                            item.row.navigate?.("/customers/customer-accesses", item.id, accessesPermission);
                        }}
                        label="Acessos"
                        icon={
                            <ManageAccountsOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />
                        }
                    />}
                    {item.row.canOpenParameters && <Chip
                        sx={{
                            fontFamily: "Prometo",
                            background: "#00B700",
                            color: "#ffff",
                            padding: 1,
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (!item.row.canOpenParameters) return;
                            item.row.openParameters(item.row);
                        }}
                        label="Parâmetros"
                        icon={<Settings style={{ height: 25, width: 25, fill: "#ffff" }} />}
                    />}
                </Box>
            )
        },
    }
];

const rowsOptions = (
    list: Array<CustomerType>,
    permissions: Array<PermissionsType>,
    navigate: (...args: any) => void,
    openParameters: (customer: CustomerType) => void,
    canOpenParameters: boolean,
): Array<CustomerType> => {
    const result = [...list];
    return result.map((item) => ({ ...item, navigate, permissions, openParameters, canOpenParameters }));
};

/**
 * CustomerPage - Component that renders a page to list, create and manage customers
 * @component
 */
const CustomerPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    //atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState(true);
    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<CustomerType>>([]);
    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    const [infoExpanded, setInfoExpanded] = useState<boolean>(true);
    const [info2Expanded, setInfo2Expanded] = useState<boolean>(false);

    // states
    const [countriesList, setCountriesList] = useState<Array<CountryType>>([]);
    const [statesList, setStatesList] = useState<Array<StateType>>([]);
    const [citiesList, setCitiesList] = useState<Array<CityType>>([]);

    const [customersList, setCustomersList] = useState<Array<CustomerType>>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerType>({
        id: -1,
    } as any);
    const [featureCustomer, setFeatureCustomer] = useState<CustomerType | null>(null);

    const [customerCountry, setCustomerCountry] = useState<string>("");
    const [customerState, setCustomerState] = useState<string>("");
    const [customerCity, setCustomerCity] = useState<string>("");
    const [customerName, setCustomerName] = useState<string>("");
    const [customerReducedName, setCustomerReducedName] = useState<string>("");

    const [useCpf, setUseCpf] = useState<boolean>(false);
    const [customerCnpj, setCustomerCnpj] = useState<string | null>("");
    const [customerCpf, setCustomerCpf] = useState<string | null>("");

    const [cep, setCep] = useState<string>("");
    const [customerAddress, setCustomerAddress] = useState<string>("");
    const [customerPhone, setCustomerPhone] = useState<string>("");
    const [customerEmail, setCustomerEmail] = useState<string>("");
    const [customerStartDate, setCustomerStartDate] = useState<string>(dayjs().toISOString());
    const [customerEndDate, setCustomerEndDate] = useState<string>(dayjs().add(1, "day").toISOString());

    const [customerImage, setCustomerImage] = useState<string | null | undefined | File | any>(null);
    const [customerImagePreview, setCustomerImagePreview] = useState<string>("");
    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    // refs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);

    // hooks e const
    const location = useLocation();
    const navigate = useNavigate();

    const isExpiredDate = useMemo(() => {
        return dayjs(selectedCustomer.endDate).isBefore(dayjs());
    }, [selectedCustomer.endDate]);

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setInEdit(false);
        setInInsert(false);
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setSelectedCustomer({} as any);
        setInEdit(false);
        setInInsert(false);
        setCustomerName("");
        setCustomerReducedName("");
        setCustomerCnpj("");
        setCustomerAddress("");
        setCustomerStartDate(dayjs().toISOString());
        setCustomerEndDate(dayjs().add(1, "day").toISOString());
        setCustomerCountry("");
        setCustomerState("");
        setCustomerCity("");
        setCustomerImage(null);
        setCustomerImagePreview("");
        setCep("");
        setCustomerEmail("");
        setCustomerPhone("");
        setInfo2Expanded(false);
        setInfoExpanded(true);
        setUseCpf(false);
        setCustomerCpf("");
    }, []);

    useEffect(() => {
        if (customerImage) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCustomerImagePreview(reader.result as string);
            };
            reader.readAsDataURL(customerImage);
        } else {
            setCustomerImagePreview("");
        }
    }, [customerImage]);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    const goToRoute = useCallback(
        async (path: string, state?: any) => {
            const hasConection: boolean = window.navigator.onLine;
            if (hasConection) {
                navigate(path, { state: state });
            } else {
                setIsOnline(false);
            }
        },
        [navigate]
    );

    const getCountriesList = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);
        try {
            const { data } = await findAllCountriesListResponse(token);
            setCountriesList(data);
        } catch (error: any) {
            console.log(error);
            setCountriesList([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    }, [cookies.token]);

    useEffect(() => {
        getCountriesList();
    }, [getCountriesList]);

    /** Endereco pelo CEP (ViaCEP, gratuito): preenche endereco, pais, estado e cidade. */
    const cepAutofill = useCepAutofill({
        cep,
        enabled: inInsert || inEdit,
        savedCep: selectedCustomer?.postalCode,
        inEdit,
        onAddress: (address) => {
            if (address.street) setCustomerAddress([address.street, address.neighborhood].filter(Boolean).join(" - ").slice(0, 100));
        },
        location: {
            countriesList,
            statesList,
            citiesList,
            country: customerCountry,
            state: customerState,
            setCountry: setCustomerCountry,
            setState: setCustomerState,
            setCity: setCustomerCity,
        },
    });
    const { pendingStateName, pendingCityName } = cepAutofill;

    const getStatesList = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const countryId = countriesList.find((item) => { if (item.name === customerCountry) return item })?.id;
        try {
            const { data } = await findAllStatesByCountryIdListResponse(
                token,
                countryId!!
            );
            setStatesList(data);
            setCustomerState(
                pendingStateName(data)
                ?? data.find((item: any) => item.id === selectedCustomer.stateId)?.name
                ?? ""
            );
        } catch (error: any) {
            console.log(error);
            setStatesList([]);
        }
    }, [cookies.token, customerCountry, countriesList, selectedCustomer.stateId, pendingStateName]);

    useEffect(() => {
        if (customerCountry) {
            getStatesList();
        }
    }, [getStatesList, customerCountry]);

    const getCityList = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const stateId = statesList.find((item) => { if (item.name === customerState) return item })?.id;
        try {
            const { data } = await findAllCitiesByStateIdListResponse(
                token,
                stateId!!
            );
            setCitiesList(data);
            setCustomerCity(
                pendingCityName(data)
                ?? data.find((item: any) => item.id === selectedCustomer.cityId)?.name
                ?? ""
            );
        } catch (error: any) {
            console.log(error);
            setCitiesList([]);
        }
    }, [cookies.token, statesList, customerState, selectedCustomer.cityId, pendingCityName]);

    useEffect(() => {
        if (customerCountry && customerState) {
            getCityList();
        }
    }, [getCityList, customerCountry, customerState]);

    const getAllCustomers = useCallback(
        async (status: string = "activated", page: number = 0, size: number = 100) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            const currentParameters: SelectedParametersType = cookies.selectedParameters;

            setLoading(true);
            try {
                const { data } = await customerfindAllCrudResponse(
                    token,
                    status,
                    currentParameters.customer.id,
                    page,
                    size
                );
                setCustomersList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);
                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setCustomersList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [cookies.token, cookies.selectedParameters]
    );

    useEffect(() => {
        getAllCustomers();
    }, [getAllCustomers]);

    const getPlacesPredictions = useCallback(async () => {
        try {
            const response = await findAllPlacesWithIqLocation(customerAddress);
            return response.data as Array<any>;
        } catch (error) {
            console.log(error);
        }
    }, [customerAddress]);


    const onSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        setLoading(true);

        const countryId = countriesList.find(
            (item) => item.name === customerCountry
        )?.id;

        const stateId = statesList.find((item) => item.name === customerState)?.id;
        const cityId = citiesList.find((item) => item.name === customerCity)?.id;

        const places: any = getPlacesPredictions();

        const data: CustomerRequestType = {
            id: -1,
            name: customerName,
            reducedName: customerReducedName,
            phone: customerPhone,
            email: customerEmail,
            cnpj: customerCnpj,
            useCpf: useCpf,
            cpf: customerCpf,
            postalCode: cep,
            countryId: countryId!!,
            stateId: stateId!!,
            cityId: cityId!!,
            address: customerAddress,
            startDate: customerStartDate,
            image: customerImagePreview,
            endDate: customerEndDate,
            managerId: currentParameters.customer.id,
            lat: places[0]?.lat ?? -15.5986686,
            lng: places[0]?.lon ?? -56.0991301
        };

        if (
            !data.name ||
            (!useCpf && !data.reducedName) ||
            (!useCpf && !data.cnpj) ||
            (useCpf && !data.cpf) ||
            !data.startDate ||
            !data.endDate ||
            !data.countryId ||
            !data.stateId ||
            !data.cityId ||
            !data.postalCode ||
            !data.address ||
            !data.phone ||
            !data.email
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await customerOnSaveData(token, data);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllCustomers();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        getPlacesPredictions,
        cookies.selectedParameters,
        citiesList,
        countriesList,
        statesList,
        cookies.token,
        customerCountry,
        customerState,
        customerCity,
        customerName,
        customerReducedName,
        customerImagePreview,
        customerCnpj,
        cep,
        customerAddress,
        customerPhone,
        customerEmail,
        customerStartDate,
        customerEndDate,
        handleClose,
        getAllCustomers,
        useCpf,
        customerCpf
    ]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const countryId = countriesList.find(
            (item) => item.name === customerCountry
        )?.id;
        const stateId = statesList.find((item) => item.name === customerState)?.id;
        const cityId = citiesList.find((item) => item.name === customerCity)?.id;

        const data: CustomerRequestType = {
            id: selectedCustomer.id,
            name: customerName,
            reducedName: customerReducedName,
            phone: customerPhone,
            email: customerEmail,
            cnpj: customerCnpj,
            useCpf: useCpf,
            cpf: customerCpf,
            postalCode: cep,
            countryId: countryId!!,
            stateId: stateId!!,
            cityId: cityId!!,
            address: customerAddress,
            startDate: customerStartDate,
            image: customerImagePreview,
            endDate: customerEndDate,
            lat: -15.5986686,
            lng: -56.0991301,

        };

        if (
            !data.name ||
            (!useCpf && !data.reducedName) ||
            (!useCpf && !data.cnpj) ||
            (useCpf && !data.cpf) ||
            !data.startDate ||
            !data.endDate ||
            !data.countryId ||
            !data.stateId ||
            !data.cityId ||
            !data.postalCode ||
            !data.address ||
            !data.phone ||
            !data.email
        ) {
            setTextValidation(
                "Todos os campos são obrigatórios, preencha e tente novamente."
            );
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            data.name === selectedCustomer.name &&
            (!data.useCpf && data.reducedName === selectedCustomer.reducedName) &&
            (!data.useCpf && data.cnpj === selectedCustomer.cnpj) &&
            dayjs(data.startDate).date() === dayjs(selectedCustomer.startDate).date() &&
            dayjs(data.endDate).date() === dayjs(selectedCustomer.endDate).date() &&
            data.countryId === selectedCustomer.countryId &&
            data.stateId === selectedCustomer.stateId &&
            data.cityId === selectedCustomer.cityId &&
            data.postalCode === selectedCustomer.postalCode &&
            data.address === selectedCustomer.address &&
            data.phone === selectedCustomer.phone &&
            data.email === selectedCustomer.email &&
            data.image === selectedCustomer.image &&
            data.useCpf === selectedCustomer.useCpf &&
            (data.useCpf && data.cpf === selectedCustomer.cpf)
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (data.address !== selectedCustomer.address) {
            const places: any = getPlacesPredictions();
            data.lat = places[0].lat ?? -15.5986686;
            data.lng = places[0].lon ?? -56.0991301;
        }

        try {
            await customerOnUpdateData(token, data, data.id);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllCustomers();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        getPlacesPredictions,
        citiesList,
        countriesList,
        statesList,
        cookies.token,
        customerCountry,
        customerState,
        customerCity,
        customerName,
        customerReducedName,
        customerImagePreview,
        customerImage,
        customerCnpj,
        cep,
        customerAddress,
        customerPhone,
        customerEmail,
        customerStartDate,
        customerEndDate,
        selectedCustomer,
        handleClose,
        getAllCustomers,
        useCpf,
        customerCpf
    ]);

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                await customerUpdateStatusById(
                    token,
                    id,
                    status
                );
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllCustomers();
                }, 8000);
            }
        },
        [cookies.token, getAllCustomers, handleClose]
    );

    const getReport = useCallback(async (type: string) => {
        setLoading(true);
        let dataBuffer = new ArrayBuffer(0);
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        let reportType = type;
        let isSimpleType = false;
        if (type.includes("simple")) {
            reportType = reportType.split(" ")[0];
            isSimpleType = true;
        };
        // simpleXlsxExport = false

        try {
            const { data } = await customerFindAllExportResponse(cookies.token,
                (customersList.length > 0 && customersList[0].activated)
                    ? "activated"
                    : "deactivated", currentParameters.customer.id, reportType, isSimpleType)

            if ((data as ArrayBuffer).byteLength === 0) {
                setTextValidation("Nenhum dado para mostrar no relatório.");
                setStatusMessage("warn");
                setFieldsIsValid(false);
            } else {
                setTextValidation("Relatório gerado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
                dataBuffer = data;
            }
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                handleClose();
                setReport(dataBuffer);
                setExportType(type);
            }, 3000);
        }
    }, [cookies.token, customersList, cookies.selectedParameters, handleClose]);

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport = (exportType === "pdf" ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    useEffect(() => {
        setFilteredList(
            getFilteredList(["reducedName", "cnpj", "email"], customersList, query)
        );
    }, [customersList, query]);

    const verifyBeforeCancel = useCallback(
        (selected: CustomerType) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                const countryId = countriesList.find(
                    (item) => item.name === customerCountry
                )?.id;
                const stateId = statesList.find(
                    (item) => item.name === customerState
                )?.id;
                const cityId = citiesList.find((item) => item.name === customerCity)?.id;

                if (
                    customerName !== selected.name ||
                    (!useCpf && customerReducedName !== selected.reducedName) ||
                    (!useCpf && customerCnpj !== selected.cnpj) ||
                    dayjs(customerStartDate).date() !==
                    dayjs(selected.startDate).date() ||
                    dayjs(customerEndDate).date() !== dayjs(selected.endDate).date() ||
                    countryId !== selected.countryId ||
                    stateId !== selected.stateId ||
                    cityId !== selected.cityId ||
                    cep !== selected.postalCode ||
                    customerAddress !== selected.address ||
                    customerPhone !== selected.phone ||
                    customerEmail !== selected.email ||
                    customerImagePreview !== selected.image ||
                    useCpf !== selected.useCpf ||
                    (useCpf && customerCpf !== selected.cpf)
                ) {
                    canCancel = false;
                }
            } else if (
                !inEdit &&
                inInsert &&
                (customerName ||
                    (!useCpf && customerReducedName) ||
                    (!useCpf && customerCnpj) ||
                    dayjs(customerEndDate).date() !==
                    dayjs().add(1, "day").date() ||
                    customerCountry ||
                    customerState ||
                    customerCity ||
                    cep ||
                    customerAddress ||
                    customerPhone ||
                    customerEmail ||
                    customerImagePreview ||
                    useCpf ||
                    (useCpf && customerCpf)
                )
            ) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box
                                    className="flex flex-column align-items-left"
                                    style={{ flex: "1" }}
                                >
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">
                                        {props?.message.detail}
                                    </Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [
            inEdit,
            inInsert,
            handleClose,
            statusMessage,
            isToastVisible,
            cep,
            citiesList,
            countriesList,
            customerAddress,
            customerStartDate,
            customerCity,
            customerEndDate,
            customerCountry,
            customerCnpj,
            customerEmail,
            customerName,
            customerReducedName,
            customerImagePreview,
            customerPhone,
            customerState,
            statesList,
            useCpf,
            customerCpf
        ]
    );

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box
                                className="flex flex-column align-items-left"
                                style={{ flex: "1" }}
                            >
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">
                                    {props?.message.detail}
                                </Box>

                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>

                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const handleOnEditValues = useCallback(async (selectedCustomer: CustomerType) => {
        if (selectedCustomer?.name && !inInsert) {
            setCustomerName(selectedCustomer.name);
            setCustomerReducedName(selectedCustomer?.reducedName);
            setCustomerCnpj(selectedCustomer?.cnpj);
            setCustomerAddress(selectedCustomer.address);
            setCustomerStartDate(dayjs(selectedCustomer.startDate).toISOString());
            setCustomerEndDate(dayjs(selectedCustomer.endDate).toISOString());
            setCustomerCountry(countriesList.find((item) => item.id === selectedCustomer.countryId)?.name ?? "");
            setCep(selectedCustomer.postalCode);
            setCustomerPhone(selectedCustomer.phone);
            setCustomerEmail(selectedCustomer.email);
            setCustomerImagePreview(selectedCustomer.image!!);
            setCustomerCpf(selectedCustomer?.cpf);
            setUseCpf(selectedCustomer.useCpf);
        }
    }, [
        inInsert,
        countriesList,
        statesList,
        citiesList,
    ]);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid
                className="container "
                sx={{
                    height: 650,
                    width: 950,
                }}
            >
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 650, padding: 5 }}>
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);


    const IOSSwitch = useMemo(() => styled((props: SwitchProps) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#65C466',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    })), []);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);

                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                    sx={style}
                >
                    <Box
                        className="row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                            "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                        }}
                    >
                        {!loading ? (
                            <>
                                {!info2Expanded && (
                                    <>
                                        <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                            <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                                Identificação
                                            </Typography>
                                        </Box>
                                        <Box
                                            className="col-sm-12 col-md-5"
                                            style={{ display: "flex", justifyContent: "end" }}
                                        >
                                            {selectedCustomer?.name && (
                                                <>
                                                    <Chip
                                                        style={{
                                                            color: selectedCustomer.activated
                                                                ? "#00B700"
                                                                : "#dc3545",
                                                            marginRight: 5,
                                                            width: "50%",
                                                            fontWeight: "bolder",
                                                        }}
                                                        clickable={false}
                                                        onClick={() => { }}
                                                        label={
                                                            selectedCustomer.activated ? "Ativo" : "Inativo"
                                                        }
                                                    />
                                                    {selectedCustomer.activated && (
                                                        <Chip
                                                            style={{
                                                                color: "black",
                                                                marginRight: 5,
                                                                width: "50%",
                                                            }}
                                                            disabled={!location?.state?.canEdit}
                                                            onClick={() => {
                                                                setInEdit(true);
                                                            }}
                                                            label="Editar"
                                                            icon={
                                                                <DriveFileRenameOutlineOutlined
                                                                    style={{
                                                                        height: 24,
                                                                        width: 24,
                                                                        fill: "black",
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    )}
                                                    {false && (
                                                        <Chip
                                                            style={{
                                                                color: "black",
                                                                marginRight: 5,
                                                                width: "50%",
                                                            }}
                                                            disabled={!location?.state?.canDelete}
                                                            onClick={() => {
                                                                if (!isToastVisible) {
                                                                    handleClose();
                                                                }
                                                            }}
                                                            label="Excluir"
                                                            icon={
                                                                <DeleteForeverOutlined
                                                                    style={{
                                                                        height: 24,
                                                                        width: 24,
                                                                        fill: "black",
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    )}

                                                    <Chip
                                                        style={{
                                                            color: "black",
                                                            marginRight: 5,
                                                            width: "50%",
                                                        }}
                                                        disabled={!location?.state?.canEdit}
                                                        onClick={() => {
                                                            if (!isToastVisible) {
                                                                verifyBeforeUpdateStatus(
                                                                    selectedCustomer.id,
                                                                    selectedCustomer.activated
                                                                        ? "deactivate"
                                                                        : "activate"
                                                                );
                                                            }
                                                        }}
                                                        label={
                                                            selectedCustomer.activated ? "Desativar" : "Ativar"
                                                        }
                                                        icon={
                                                            <RemoveCircleOutlineOutlined
                                                                style={{ height: 24, width: 24, fill: "black" }}
                                                            />
                                                        }
                                                    />
                                                </>
                                            )}
                                        </Box>
                                        {isExpiredDate && <Box className={"col-md-12"} style={{ marginTop: 5, marginBottom: 5 }}>
                                            <Alert
                                                style={{
                                                    borderRadius: 5,
                                                    padding: 10
                                                }}
                                                severity="info"
                                            >
                                                {"O cliente está com a data de contrato expirada."}
                                            </Alert>
                                        </Box>}
                                    </>
                                )}
                                <Accordion
                                    expanded={infoExpanded}
                                    style={{ padding: 0, margin: 0, boxShadow: "none" }}
                                >
                                    <AccordionSummary
                                        style={{ margin: 0, boxShadow: "none", display: "none" }}
                                        aria-controls="panel2-content"
                                        id="panel2-header"
                                    ></AccordionSummary>
                                    <AccordionDetails>
                                        <Box className="row">

                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormGroup>
                                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                        <IOSSwitch
                                                            title="Usar CPF para cadastro"
                                                            checked={useCpf}
                                                            disabled={!getCurrentUser.configs.isManager || (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean}
                                                            onChange={(event) => {
                                                                const checked = event.target.checked;

                                                                if (!checked) {
                                                                    setCustomerCpf("");
                                                                }
                                                                setCustomerCnpj("");
                                                                setUseCpf(checked);
                                                            }}
                                                            inputProps={{ 'aria-label': 'ant design' }}
                                                        />
                                                        <Typography style={{ color: "gray" }}>Usa CPF</Typography>
                                                    </Stack>
                                                </FormGroup>
                                            </Box>

                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-name-label"
                                                        shrink
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        style={{ fontFamily: "Prometo" }}
                                                        required
                                                    >
                                                        Nome
                                                    </InputLabel>
                                                    <TextField
                                                        name="name"
                                                        InputProps={{
                                                            label: "Nome",
                                                            notched: true,
                                                            style: { fontFamily: "Prometo" },
                                                        }}
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={customerName}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCustomerName(event.target.value);
                                                        }}
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-login"
                                                        placeholder="Digite o nome"
                                                        required
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(event: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PersonOutlineOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            {!useCpf && <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-name-label"
                                                        shrink
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        style={{ fontFamily: "Prometo" }}
                                                        required
                                                    >
                                                        Nome fantasia
                                                    </InputLabel>
                                                    <TextField
                                                        name="reducedName"
                                                        InputProps={{
                                                            label: "Nome fantasia",
                                                            notched: true,
                                                            style: { fontFamily: "Prometo" },
                                                        }}
                                                        required
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={customerReducedName}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCustomerReducedName(event.target.value);
                                                        }}
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-login"
                                                        placeholder="Digite o nome fantasia"
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(event: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PersonOutlineOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>}

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                {!useCpf && <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-cpf-label"
                                                        required
                                                        shrink
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        CNPJ
                                                    </InputLabel>
                                                    <TextField
                                                        name="cnpj"
                                                        type="cnpj"
                                                        autoComplete="off"
                                                        required
                                                        inputProps={{ maxLength: 14 }}
                                                        InputProps={{ label: "CNPJ", notched: true }}
                                                        placeholder="Digite o CNPJ"
                                                        className="form-control customer-cnpj"
                                                        value={CNPJFormatter(customerCnpj)}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCustomerCnpj(event.target.value);
                                                        }}
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PinOutlined />
                                                    </IconButton>
                                                </FormControl>}

                                                {useCpf &&
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex", width: "100%" }}
                                                    >
                                                        <InputLabel
                                                            id="demo-multiple-cpf-label"
                                                            shrink
                                                            required
                                                            disabled={
                                                                (selectedCustomer?.name &&
                                                                    !inEdit &&
                                                                    !inInsert) as boolean
                                                            }
                                                        >
                                                            CPF
                                                        </InputLabel>
                                                        <TextField
                                                            name="cpf"
                                                            type="cpf"
                                                            required
                                                            autoComplete="off"
                                                            inputProps={{ maxLength: 11 }}
                                                            InputProps={{ label: "CPF", notched: true }}
                                                            placeholder="Digite o CPF"
                                                            className="form-control driver-cpf"
                                                            value={cpfFormatter(customerCpf ?? "")}
                                                            onChange={(
                                                                event: React.ChangeEvent<HTMLInputElement>
                                                            ) => {
                                                                setCustomerCpf(event.target.value);
                                                            }}
                                                            disabled={
                                                                (selectedCustomer?.name &&
                                                                    !inEdit &&
                                                                    !inInsert) as boolean
                                                            }
                                                        />
                                                        <IconButton
                                                            className="visibility-password"
                                                            disabled
                                                            onMouseDown={(e: SyntheticEvent) => { }}
                                                            edge="end"
                                                            size="large"
                                                        >
                                                            <PinOutlined />
                                                        </IconButton>
                                                    </FormControl>
                                                }
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <Autocomplete
                                                    id="chip-label-select-country"
                                                    className="input-select-country"
                                                    style={{
                                                        width: "100%",
                                                        minHeight: "56px",
                                                        height: "56px",
                                                        backgroundColor: "#ffffff29",
                                                        borderColor: "lightgray",
                                                    }}
                                                    options={countriesList}
                                                    value={countriesList.find(a => a.name === customerCountry) ?? null}
                                                    onChange={(_, option) => {
                                                        setCustomerCountry(option?.name ?? "");
                                                    }}
                                                    getOptionLabel={(option) => `${option.name}`}
                                                    noOptionsText={"Nada encontrado"}
                                                    renderInput={(params) => (
                                                        <>
                                                            <FormControl
                                                                className="form-group"
                                                                style={{ display: "flex" }}
                                                            >
                                                                <InputLabel
                                                                    id="country-label"
                                                                    required
                                                                    style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                                    shrink
                                                                    disabled={
                                                                        (selectedCustomer?.name &&
                                                                            !inEdit &&
                                                                            !inInsert) as boolean
                                                                    }
                                                                >
                                                                    País
                                                                </InputLabel>
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    placeholder="Selecione um país"
                                                                    autoComplete="off"
                                                                    required
                                                                    InputLabelProps={{ shrink: true }}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        notched: true,
                                                                        label: "País",
                                                                        classes: {
                                                                            notchedOutline: "custom-notch",
                                                                        },
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </>
                                                    )}
                                                    disabled={
                                                        (selectedCustomer?.name &&
                                                            !inEdit &&
                                                            !inInsert) as boolean
                                                    }
                                                />
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <Autocomplete
                                                    id="chip-label-select-state"
                                                    className="input-select-state"
                                                    style={{
                                                        width: "100%",
                                                        minHeight: "56px",
                                                        height: "56px",
                                                        backgroundColor: "#ffffff29",
                                                        borderColor: "lightgray",
                                                    }}
                                                    options={statesList}
                                                    value={statesList.find(a => a.name === customerState) ?? null}
                                                    onChange={(_, option) => {
                                                        setCustomerState(option?.name ?? "");
                                                    }}
                                                    getOptionLabel={(option) => `${option.name}`}
                                                    noOptionsText={"Nada encontrado"}
                                                    renderInput={(params) => (
                                                        <>
                                                            <FormControl
                                                                className="form-group"
                                                                style={{ display: "flex" }}
                                                            >
                                                                <InputLabel
                                                                    id="state-label"
                                                                    required
                                                                    style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                                    shrink
                                                                    disabled={
                                                                        (selectedCustomer?.name &&
                                                                            !inEdit &&
                                                                            !inInsert) as boolean
                                                                    }
                                                                >
                                                                    Estado
                                                                </InputLabel>
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    placeholder="Selecione um estado"
                                                                    autoComplete="off"
                                                                    required
                                                                    InputLabelProps={{ shrink: true }}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        notched: true,
                                                                        label: "Estado",
                                                                        classes: {
                                                                            notchedOutline: "custom-notch",
                                                                        },
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </>
                                                    )}
                                                    disabled={
                                                        (selectedCustomer?.name &&
                                                            !inEdit &&
                                                            !inInsert) as boolean
                                                    }
                                                />
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <Autocomplete
                                                    id="chip-label-select-city"
                                                    className="input-select-city"
                                                    style={{
                                                        width: "100%",
                                                        minHeight: "56px",
                                                        height: "56px",
                                                        backgroundColor: "#ffffff29",
                                                        borderColor: "lightgray",
                                                    }}
                                                    options={citiesList}
                                                    value={citiesList.find(a => a.name === customerCity) ?? null}
                                                    onChange={(_, option) => {
                                                        setCustomerCity(option?.name ?? "");
                                                    }}
                                                    getOptionLabel={(option) => `${option.name}`}
                                                    noOptionsText={"Nada encontrado"}
                                                    renderInput={(params) => (
                                                        <>
                                                            <FormControl
                                                                className="form-group"
                                                                style={{ display: "flex" }}
                                                            >
                                                                <InputLabel
                                                                    id="city-label"
                                                                    required
                                                                    style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                                    shrink
                                                                    disabled={
                                                                        (selectedCustomer?.name &&
                                                                            !inEdit &&
                                                                            !inInsert) as boolean
                                                                    }
                                                                >
                                                                    Município
                                                                </InputLabel>
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    placeholder="Selecione um município"
                                                                    autoComplete="off"
                                                                    required
                                                                    InputLabelProps={{ shrink: true }}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        notched: true,
                                                                        label: "Município",
                                                                        classes: {
                                                                            notchedOutline: "custom-notch",
                                                                        },
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </>
                                                    )}
                                                    disabled={
                                                        (selectedCustomer?.name &&
                                                            !inEdit &&
                                                            !inInsert) as boolean
                                                    }
                                                />
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-cep-label"
                                                        shrink
                                                        required
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        CEP
                                                    </InputLabel>
                                                    <TextField
                                                        name="cep"
                                                        type="text"
                                                        required
                                                        autoComplete="off"
                                                        InputProps={{ label: "CEP", notched: true }}
                                                        inputProps={{ maxLength: 8 }}
                                                        placeholder="Digite o cep"
                                                        className="form-control customer-cep"
                                                        value={formatterZipCode(cep)}
                                                        error={cepAutofill.hasError}
                                                        helperText={cepAutofill.helperText}
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCep(event.target.value);
                                                        }}
                                                        onBlur={cepAutofill.onBlur}
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <LocalAirportOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-address-label"
                                                        shrink
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        style={{ fontFamily: "Prometo" }}
                                                        required
                                                    >
                                                        Endereço
                                                    </InputLabel>
                                                    <TextField
                                                        name="address"
                                                        InputProps={{
                                                            label: "Endereço",
                                                            notched: true,
                                                            style: { fontFamily: "Prometo" },
                                                        }}
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={customerAddress}
                                                        onChange={(
                                                            event: ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCustomerAddress(event.target.value);
                                                        }}
                                                        type="text"
                                                        autoComplete="off"
                                                        className="form-control form-control-login"
                                                        placeholder="Digite o endereço"
                                                    />
                                                    <IconButton
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <PlaceOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>

                                <Box
                                    className="crud-additional-info-bar col-sm-12 col-md-7 d-none d-md-flex"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        background: "#f3f4f6",
                                    }}
                                    onClick={() => {
                                        setInfo2Expanded(!info2Expanded);
                                        setInfoExpanded(!infoExpanded);
                                    }}
                                >
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Informações adicionais
                                    </Typography>
                                    <Tooltip
                                        title={
                                            "Clique para adicionar informações adicionais sobre o cliente, contrato e etc."
                                        }
                                        style={{ color: "black", margin: 5, cursor: "pointer" }}
                                    >
                                        <HelpOutlineOutlined />
                                    </Tooltip>
                                </Box>

                                <Box
                                    className="crud-additional-info-bar col-sm-12 col-md-5"
                                    style={{
                                        display: "flex",
                                        justifyContent: "end",
                                        cursor: "pointer",
                                        background: "#f3f4f6",
                                    }}
                                    onClick={() => {
                                        setInfo2Expanded(!info2Expanded);
                                        setInfoExpanded(!infoExpanded);
                                    }}
                                >
                                    <IconButton onClick={() => { }} edge="end" size="large">
                                        <ArrowDropDownIcon
                                            style={{
                                                transform: info2Expanded ? "rotate(180deg)" : "",
                                            }}
                                        />
                                    </IconButton>
                                </Box>

                                <Accordion
                                    expanded={info2Expanded}
                                    style={{
                                        padding: 0,
                                        margin: 0,
                                        boxShadow: "none",
                                        marginTop: 5,
                                    }}
                                >
                                    <IconButton
                                        aria-label="toggle accordion"
                                        className="d-none"
                                        onClick={() => { }}
                                        edge="end"
                                        size="large"
                                    >
                                        <ArrowDropDownIcon />
                                    </IconButton>
                                    <AccordionDetails>
                                        <Box className="row">
                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >

                                                <InputLabel
                                                    id="demo-multiple-address-label"
                                                    shrink
                                                    disabled={
                                                        (selectedCustomer?.name &&
                                                            !inEdit &&
                                                            !inInsert) as boolean
                                                    }
                                                    style={{ fontFamily: "Prometo" }}
                                                >
                                                    Imagem (Opcional)
                                                </InputLabel>
                                            </Box>
                                            <Box
                                                className="col-md-6"
                                                style={{ margin: "15px 0px 15px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <Paper
                                                        style={{
                                                            padding: 5,
                                                            boxShadow: "none",
                                                            border: "none",
                                                            display: "flex",
                                                            height: 200,
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            alignContent: "center",
                                                        }}
                                                    >
                                                        <Box className="form-group" style={{ padding: 10 }}>
                                                            <input
                                                                type="file"
                                                                style={{ display: "none" }}
                                                                ref={fileInputRef}
                                                                accept="image/*"
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const target = event.target;
                                                                    const file = target.files;
                                                                    if (file && file[0].type.substring(0, 5) === "image") {
                                                                        setCustomerImage(file[0]);
                                                                    } else {
                                                                        setCustomerImage(null);
                                                                    }
                                                                }}
                                                            />
                                                            {customerImagePreview ? (
                                                                <>
                                                                    <img
                                                                        src={customerImagePreview}
                                                                        alt={"preview"}
                                                                        style={{
                                                                            objectFit: "contain",
                                                                            width: 200,
                                                                            height: 200,
                                                                            display: "flex",
                                                                            boxShadow: "none",
                                                                            borderRadius: "5px 5px 0px 0px",
                                                                        }}
                                                                    />

                                                                    <Box
                                                                        className="container-image-chip"
                                                                        style={{
                                                                            width: 200,
                                                                            height: 40,
                                                                            display: "flex",
                                                                            padding: 5,
                                                                            justifyContent: "center",
                                                                            alignItems: "center",
                                                                            backgroundImage: `url(${customerImagePreview})`,
                                                                            borderRadius: "0px 0px 5px 5px",
                                                                        }}
                                                                    >
                                                                        <Chip
                                                                            className="chip-image"
                                                                            style={{ color: "#ffff" }}
                                                                            disabled={
                                                                                !inInsert && !inEdit
                                                                            }
                                                                            onClick={() => {
                                                                                setCustomerImage(null);
                                                                                setCustomerImagePreview("");
                                                                            }}
                                                                            label="Remover"
                                                                            icon={
                                                                                <HideImageOutlined
                                                                                    style={{ height: 24, width: 24, fill: "#ffff" }}
                                                                                />
                                                                            }
                                                                        />
                                                                    </Box>
                                                                </>
                                                            ) : (
                                                                <Box style={{ display: "flex" }}>
                                                                    <Chip
                                                                        style={{ color: "black", width: selectedCustomer.id ? -1 : 200 }}
                                                                        disabled={!inEdit && !inInsert}
                                                                        onClick={(event: any) => {
                                                                            event.preventDefault();
                                                                            if (fileInputRef.current) {
                                                                                fileInputRef.current.click();
                                                                            }
                                                                        }}
                                                                        label="Anexar"
                                                                        icon={
                                                                            <AttachFileOutlined
                                                                                style={{ height: 24, width: 24, fill: "black" }}
                                                                            />
                                                                        }
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                </FormControl>
                                            </Box>

                                            <Box
                                                className="col-md-6"
                                            >
                                                <Box
                                                    className="col-md-12"
                                                    style={{ margin: "15px 0px 25px 0px" }}
                                                >
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex", width: "100%" }}
                                                    >
                                                        <LocalizationProvider
                                                            dateAdapter={AdapterDayjs}
                                                            adapterLocale={"pt-br"}
                                                        >
                                                            <DatePicker
                                                                sx={{ width: "100%" }}
                                                                label={"Data início de contrato"}
                                                                format="DD/MM/YYYY"
                                                                onChange={(value) => {
                                                                    if (value && !value.isValid()) return;
                                                                    setCustomerStartDate(
                                                                        value?.toISOString() ?? dayjs().toISOString()
                                                                    );
                                                                }}

                                                                value={dayjs(customerStartDate)}
                                                                disabled={
                                                                    (selectedCustomer?.startDate &&
                                                                        !inEdit &&
                                                                        !inInsert) as boolean
                                                                }
                                                                minDate={customerStartDate ? dayjs(customerStartDate) : dayjs()}
                                                                slotProps={{
                                                                    textField: {
                                                                        required: true,
                                                                    },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </FormControl>
                                                </Box>

                                                <Box
                                                    className="col-md-12"
                                                    style={{ margin: "15px 0px 25px 0px" }}
                                                >
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex", width: "100%" }}
                                                    >
                                                        <LocalizationProvider
                                                            dateAdapter={AdapterDayjs}
                                                            adapterLocale={"pt-br"}
                                                        >
                                                            <DatePicker
                                                                sx={{ width: "100%" }}
                                                                label={"Data término contrato"}
                                                                format="DD/MM/YYYY"
                                                                onChange={(value) => {
                                                                    if (value && !value.isValid()) return;
                                                                    setCustomerEndDate(
                                                                        value?.toISOString() ?? dayjs().toISOString()
                                                                    );
                                                                }}
                                                                value={dayjs(customerEndDate)}
                                                                disabled={
                                                                    (selectedCustomer?.startDate &&
                                                                        !inEdit &&
                                                                        !inInsert) as boolean
                                                                }
                                                                minDate={dayjs(customerStartDate).add(1, "day")}
                                                                disablePast
                                                                slotProps={{
                                                                    textField: {
                                                                        required: true,
                                                                    },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </FormControl>
                                                </Box>

                                                <Box
                                                    className="col-md-12"
                                                    style={{ margin: "15px 0px 25px 0px" }}
                                                >
                                                    <FormControl
                                                        className="form-group"
                                                        style={{ display: "flex", width: "100%" }}
                                                    >
                                                        <InputLabel
                                                            id="demo-multiple-name-label"
                                                            required
                                                            shrink
                                                            disabled={
                                                                (selectedCustomer?.name &&
                                                                    !inEdit &&
                                                                    !inInsert) as boolean
                                                            }
                                                        >
                                                            Telefone
                                                        </InputLabel>
                                                        <TextField
                                                            name="phoneNumber"
                                                            type="tel"
                                                            autoComplete="off"
                                                            required
                                                            InputProps={{ label: "Telefone", notched: true }}
                                                            inputProps={{ maxLength: 11 }}
                                                            className="form-control phone"
                                                            placeholder="Digite o telefone"
                                                            inputMode="numeric"
                                                            value={phoneNumberFormatter(customerPhone)}
                                                            onChange={(
                                                                event: React.ChangeEvent<HTMLInputElement>
                                                            ) => {
                                                                setCustomerPhone(event.target.value);
                                                            }}
                                                            disabled={
                                                                (selectedCustomer?.name &&
                                                                    !inEdit &&
                                                                    !inInsert) as boolean
                                                            }
                                                        />
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            className="visibility-password"
                                                            disabled
                                                            onMouseDown={(e: SyntheticEvent) => { }}
                                                            edge="end"
                                                            size="large"
                                                        >
                                                            <PhoneOutlined />
                                                        </IconButton>
                                                    </FormControl>
                                                </Box>

                                            </Box>

                                            <Box
                                                className="col-md-12"
                                                style={{ margin: "15px 0px 20px 0px" }}
                                            >
                                                <FormControl
                                                    className="form-group"
                                                    style={{ display: "flex", width: "100%" }}
                                                >
                                                    <InputLabel
                                                        id="demo-multiple-mail-label"
                                                        shrink
                                                        required
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                    >
                                                        E-mail
                                                    </InputLabel>
                                                    <TextField
                                                        name="email"
                                                        type="email"
                                                        autoComplete="off"
                                                        InputProps={{ label: "E-mail", notched: true }}
                                                        placeholder="Digite o e-mail"
                                                        className="form-control form-control-login"
                                                        disabled={
                                                            (selectedCustomer?.name &&
                                                                !inEdit &&
                                                                !inInsert) as boolean
                                                        }
                                                        value={customerEmail}
                                                        required
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setCustomerEmail(event.target.value);
                                                        }}
                                                    />
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        className="visibility-password"
                                                        disabled
                                                        onMouseDown={(e: SyntheticEvent) => { }}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        <EmailOutlined />
                                                    </IconButton>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", width: "100%", alignSelf: "stretch", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0, 0, 0, 0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedCustomer);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={!inEdit && !inInsert}
                                onClick={() => {
                                    inInsert && !inEdit ? onSave() : onUpdate();
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        cepAutofill.hasError,
        cepAutofill.helperText,
        cepAutofill.onBlur,
        handleClose,
        verifyBeforeCancel,
        customerImage,
        customerCountry,
        customerState,
        customerCity,
        customerName,
        customerReducedName,
        customerCpf,
        customerCnpj,
        cep,
        customerAddress,
        customerPhone,
        customerEmail,
        customerStartDate,
        customerEndDate,
        isExpiredDate,
        customerImage,
        customerImagePreview,
        verifyBeforeUpdateStatus,
        infoExpanded,
        info2Expanded,
        getCurrentUser.configs.isManager,
        location?.state?.canDelete,
        location?.state?.canEdit,
        isToastVisible,
        validationText,
        selectedCustomer,
        isOpenDetails,
        loading,
        loadingEsqueleton,
        inInsert,
        inEdit,
        countriesList,
        statesList,
        citiesList,
        onSave,
        onUpdate,
        useCpf,
        IOSSwitch
    ]);

    return (
        <>
            <CustomerFeaturesModal customer={featureCustomer} onClose={() => setFeatureCustomer(null)} />
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName="Clientes"
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList, getCurrentUser.permissions, (route: string, customerId: number, permissions: PermissionsType) => {
                    goToRoute(route, { permissions, customerId, customerName: customersList.find(it => it.id === customerId)?.name });
                }, setFeatureCustomer, getCurrentUser.configs.isManager && location?.state?.canEdit === true)}
                sortField="name"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Nome fantasia, CNPJ/CPF ou e-mail"}
                onRefresh={getAllCustomers}
                onExport={getReport}
                disableAddButton={!location?.state?.canCreate}
                withNavigation={true}
                handleNext={(activated: string, page: number, size: number) => getAllCustomers(activated, page, size)}
                handlePrevious={() => { }}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedCustomer(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
            />
        </>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(94vw, 1100px)" },
    maxWidth: 1100,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default CustomerPage;
